import { useState } from 'react';
import { ArrowRightLeft } from 'lucide-react';
import { ToolLayout } from '../components/Layout/ToolLayout';

type Mode = 'encode' | 'decode';

export function Base64Tool() {
  const [input, setInput] = useState('');
  const [mode, setMode] = useState<Mode>('encode');
  const [output, setOutput] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const handleSwap = () => {
    setMode((prev) => (prev === 'encode' ? 'decode' : 'encode'));
    // Swap input and output
    setInput(output);
    setOutput(input);
    setError(null);
  };

  const handleInputChange = (value: string) => {
    setInput(value);
    // Auto-process on input change
    if (!value.trim()) {
      setOutput('');
      setError(null);
      return;
    }

    try {
      if (mode === 'encode') {
        const encoded = btoa(unescape(encodeURIComponent(value)));
        setOutput(encoded);
        setError(null);
      } else {
        const decoded = decodeURIComponent(escape(atob(value.trim())));
        setOutput(decoded);
        setError(null);
      }
    } catch (err) {
      setError(
        mode === 'decode'
          ? 'Invalid Base64 string. Please check the input.'
          : 'Failed to encode. Please check the input.'
      );
      setOutput('');
    }
  };

  const renderOutput = () => {
    if (error) {
      return (
        <div className="text-red-400">
          <span className="font-semibold">Error:</span> {error}
        </div>
      );
    }

    if (!output) {
      return null;
    }

    return (
      <pre className="text-sm whitespace-pre-wrap break-all">{output}</pre>
    );
  };

  return (
    <ToolLayout
      title="Base64 Encoder/Decoder"
      description="Encode and decode Base64 strings"
      inputLabel={mode === 'encode' ? 'Text to Encode' : 'Base64 to Decode'}
      inputPlaceholder={
        mode === 'encode'
          ? 'Enter text to encode to Base64...'
          : 'Enter Base64 string to decode...'
      }
      inputValue={input}
      onInputChange={handleInputChange}
      outputLabel={mode === 'encode' ? 'Encoded Base64' : 'Decoded Text'}
      output={renderOutput()}
      onClear={() => {
        setOutput('');
        setError(null);
      }}
      privacyNote={true}
      actions={
        <>
          {/* Mode selector */}
          <div className="flex items-center rounded-lg border border-[var(--border-color)] overflow-hidden">
            <button
              type="button"
              onClick={() => {
                setMode('encode');
                setInput('');
                setOutput('');
                setError(null);
              }}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                mode === 'encode'
                  ? 'bg-[var(--accent)] text-[var(--bg-primary)]'
                  : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
            >
              Encode
            </button>
            <button
              type="button"
              onClick={() => {
                setMode('decode');
                setInput('');
                setOutput('');
                setError(null);
              }}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                mode === 'decode'
                  ? 'bg-[var(--accent)] text-[var(--bg-primary)]'
                  : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
            >
              Decode
            </button>
          </div>

          {/* Swap button */}
          {output && (
            <button
              type="button"
              onClick={handleSwap}
              className="btn-secondary flex items-center gap-2"
              title="Swap input and output"
            >
              <ArrowRightLeft className="w-4 h-4" />
              Swap
            </button>
          )}
        </>
      }
    />
  );
}
